import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/useAuth';
import apiService from '../../utils/api';
// CSS handled by global theme.css
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
import { Plus, Edit, Trash2, X, Filter, ChevronLeft, ChevronRight, Check } from 'lucide-react';
const TODAY_DATE = new Date().toISOString().split('T')[0];

const MilkPurchase = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [purchases, setPurchases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [milkVendors, setMilkVendors] = useState([]);

  // View state
  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    supplier_name: ''
  });

  // Form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor_id: '',
    supplier_name: '',
    cow_price: '',
    buffalo_price: '',
    cow_quantity: '',
    buffalo_quantity: '',
    notes: ''
  });

  // Load purchases on component mount and when filters change
  useEffect(() => {
    loadPurchases();
    loadSummary();
  }, [currentPage, filters]);

  useEffect(() => {
    apiService.getVendors({ vendor_type: 'milk' })
      .then(r => setMilkVendors(r.data || []))
      .catch(() => {});
  }, []);

  const loadPurchases = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 20,
        ...filters
      };
      
      const response = await apiService.getMilkPurchases(params);
      setPurchases(response.purchases);
      setTotalPages(response.pages);
      setTotalRecords(response.total);
      setError(null);
    } catch (err) {
      setError('Failed to load milk purchases');
      console.error('Error loading purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await apiService.getMilkPurchaseSummary(filters);
      setSummary(response);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.vendor_id && !formData.supplier_name.trim()) {
      toast.warning('Please select a vendor or enter a supplier name');
      return;
    }

    if (!formData.cow_price || !formData.buffalo_price) {
      toast.warning('Both cow and buffalo prices are required');
      return;
    }

    const cowQty = parseFloat(formData.cow_quantity) || 0;
    const buffaloQty = parseFloat(formData.buffalo_quantity) || 0;

    if (cowQty <= 0 && buffaloQty <= 0) {
      toast.warning('At least one quantity (cow or buffalo) must be greater than 0');
      return;
    }
    
    try {
      setLoading(true);
      
      const submitData = {
        date: formData.date,
        supplier_name: formData.vendor_id ? undefined : formData.supplier_name,
        vendor_id: formData.vendor_id ? parseInt(formData.vendor_id) : undefined,
        cow_quantity: cowQty,
        buffalo_quantity: buffaloQty,
        cow_price: parseFloat(formData.cow_price),
        buffalo_price: parseFloat(formData.buffalo_price),
        notes: formData.notes
      };
      
      if (editingPurchase) {
        await apiService.updateMilkPurchase(editingPurchase.purchase_id, submitData);
      } else {
        await apiService.createMilkPurchase(submitData);
      }
      
      // Reset form and reload data
      resetForm();
      await loadPurchases();
      await loadSummary();
      setError(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save purchase record');
      console.error('Error saving purchase:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingPurchase(purchase);
    setFormData({
      date: purchase.date,
      vendor_id: purchase.vendor_id ? purchase.vendor_id.toString() : '',
      supplier_name: purchase.supplier_name,
      cow_price: purchase.cow_price.toString(),
      buffalo_price: purchase.buffalo_price.toString(),
      cow_quantity: purchase.cow_quantity.toString(),
      buffalo_quantity: purchase.buffalo_quantity.toString(),
      notes: purchase.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to delete this purchase record?')) {
      return;
    }
    
    try {
      setLoading(true);
      await apiService.deleteMilkPurchase(purchaseId);
      await loadPurchases();
      await loadSummary();
    } catch (err) {
      toast.error(err.message || 'Failed to delete purchase record');
      console.error('Error deleting purchase:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      vendor_id: '',
      supplier_name: '',
      cow_price: '',
      buffalo_price: '',
      cow_quantity: '',
      buffalo_quantity: '',
      notes: ''
    });
    setEditingPurchase(null);
    setShowForm(false);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      supplier_name: ''
    });
    setCurrentPage(1);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  if (loading && purchases.length === 0) {
    return renderLayout(
        <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading milk purchases...</p>
        </div>
    );
  }

  // Form Modal
  const renderFormModal = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{editingPurchase ? 'Edit Purchase Record' : 'Add New Purchase Record'}</h3>
          <button onClick={resetForm} className="btn-icon">
            <X size={24} />
          </button>
        </div>
        
        <div className="modal-body">
            <form onSubmit={handleFormSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input
                    type="date" max={TODAY_DATE}
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Vendor</label>
                  <select
                    className="form-select"
                    value={formData.vendor_id}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      const vendor = milkVendors.find(v => v.vendor_id.toString() === vendorId);
                      setFormData(prev => ({
                        ...prev,
                        vendor_id: vendorId,
                        supplier_name: vendor ? vendor.name : prev.supplier_name
                      }));
                    }}
                  >
                    <option value="">-- Select Vendor (optional) --</option>
                    {sortedByName(milkVendors).map(v => (
                      <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {!formData.vendor_id && (
                <div className="form-group">
                  <label className="form-label">Supplier Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({...formData, supplier_name: e.target.value})}
                    placeholder="Enter supplier name (if not in vendor list)"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cow Price (per liter)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.cow_price}
                    onChange={(e) => setFormData({...formData, cow_price: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Buffalo Price (per liter)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.buffalo_price}
                    onChange={(e) => setFormData({...formData, buffalo_price: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Cow Quantity (liters)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.cow_quantity}
                    onChange={(e) => setFormData({...formData, cow_quantity: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Buffalo Quantity (liters)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.buffalo_quantity}
                    onChange={(e) => setFormData({...formData, buffalo_quantity: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  className="form-textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this purchase"
                  rows="3"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                   <Check size={18} />
                  {loading ? 'Saving...' : (editingPurchase ? 'Update' : 'Add Purchase')}
                </button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );

  return renderLayout(
    <>
      {showForm && renderFormModal()}

      <div className="page-header">
        <h1 className="page-title">Milk Purchase Records</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={18} /> Add New Purchase
        </button>
      </div>

      {error && (
        <div className="error-container">
          <p>{error}</p>
          <button className="btn btn-secondary btn-sm" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          <div className="card" style={{ borderLeft: '4px solid var(--primary-color)' }}>
            <h3 style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Total Quantity</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary-color)' }}>{summary.total_quantity.toFixed(2)} L</p>
          </div>
          <div className="card" style={{ borderLeft: '4px solid var(--accent-color)' }}>
            <h3 style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Total Amount</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent-color)' }}>{formatCurrency(summary.total_amount)}</p>
          </div>
          <div className="card" style={{ borderLeft: '4px solid var(--text-primary)' }}>
            <h3 style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Suppliers</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)' }}>{summary.unique_suppliers}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <div className="filter-group">
             <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Start Date</label>
            <input
                type="date" max={TODAY_DATE}
                name="start_date"
                className="form-input"
                value={filters.start_date}
                onChange={handleFilterChange}
            />
        </div>
        <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>End Date</label>
            <input
                type="date" max={TODAY_DATE}
                name="end_date"
                className="form-input"
                value={filters.end_date}
                onChange={handleFilterChange}
            />
        </div>
        <div className="filter-group">
            <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Supplier</label>
            <input
                type="text"
                name="supplier_name"
                className="form-input"
                value={filters.supplier_name}
                onChange={handleFilterChange}
                placeholder="Search supplier"
            />
        </div>
        <button onClick={clearFilters} className="btn btn-secondary" style={{ alignSelf: 'flex-end', height: '42px' }}>
          Clear
        </button>
      </div>

      {/* Purchases Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Supplier</th>
              <th>Cow Price</th>
              <th>Buffalo Price</th>
              <th>Cow Qty</th>
              <th>Buffalo Qty</th>
              <th>Total Amount</th>
              <th>Added By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((purchase) => (
              <tr key={purchase.purchase_id}>
                <td>{new Date(purchase.date).toLocaleDateString()}</td>
                <td style={{ fontWeight: 500 }}>
                  {purchase.supplier_name}
                  {purchase.vendor_id && <span style={{ fontSize: '0.75rem', color: 'var(--primary-color)', marginLeft: '4px' }}>(V)</span>}
                </td>
                <td>{formatCurrency(purchase.cow_price)}</td>
                <td>{formatCurrency(purchase.buffalo_price)}</td>
                <td>{purchase.cow_quantity.toFixed(2)} L</td>
                <td>{purchase.buffalo_quantity.toFixed(2)} L</td>
                <td style={{ fontWeight: 600, color: 'var(--success-color)' }}>{formatCurrency(purchase.total_amount)}</td>
                <td style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{purchase.creator_name}</td>
                <td>
                  <div className="actions-cell">
                      <button 
                        onClick={() => handleEdit(purchase)}
                        className="btn-icon"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      {user?.role === 'owner' && (
                        <button 
                          onClick={() => handleDelete(purchase.purchase_id)}
                          className="btn-icon"
                          style={{ color: 'var(--error-color)' }}
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {purchases.length === 0 && !loading && (
          <div className="no-data">
            <p>No milk purchase records found.</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }}>
              Add Your First Purchase Record
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <ChevronLeft size={16} />
          </button>
          
          <span style={{ margin: '0 1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </>
  );
};

export default MilkPurchase;